import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Infer } from 'superstruct';

import { createSuccessToast } from '@adflow/frontend/ui-toasts';
import { apiRequest, handleRequestErrors } from '@adflow/frontend/util-query';
import type {
	AddGMBTemplateDto,
	EditGMBTemplateDto,
	ListGMBTemplateQueryDto,
} from '@adflow/shared/data-google-my-business';
import {
	AddGoogleMyBusiness,
	CancelScheduledGoogleMyBusinessTemplate,
	CheckGMBTemplateUniqueNamesValidation,
	DeleteGoogleMyBusinessTemplates,
	GetGoogleMyBusinessLocations,
	ScheduleGoogleMyBusinessTemplate,
	UploadGoogleMyBusinessTemplate,
} from '@adflow/shared/data-google-my-business';
import type { ListGMBTemplatesHistoryQuery } from '@adflow/shared/util-history';
import {
	EditGMBTemplate,
	GetGMBTemplate,
	GetGMBTemplateHistory,
	ListGMBTemplatesWithUploadResult,
} from '@adflow/shared/util-history';

export const gmbTemplateKeys = {
	all: ['google-my-business'],

	allLists: () => [...gmbTemplateKeys.all, 'list'] as const,

	list: (query: ListGMBTemplateQueryDto) =>
		[...gmbTemplateKeys.allLists(), query] as const,

	view: (googleMyBusinessId: number) =>
		[...gmbTemplateKeys.all, 'view', googleMyBusinessId] as const,
	locations: () => [...gmbTemplateKeys.all, 'locations'] as const,
	history: (templateId: number, query?: ListGMBTemplatesHistoryQuery) =>
		[...gmbTemplateKeys.all, 'history', templateId, query] as const,
	uploadTemplate: (templateId: number) =>
		[...gmbTemplateKeys.all, 'upload', templateId] as const,
};

export function useListGMBTemplatesQuery(
	query: ListGMBTemplateQueryDto,
	enabled = true
) {
	return useQuery({
		queryKey: gmbTemplateKeys.list(query),
		queryFn: ({ signal }) =>
			apiRequest(ListGMBTemplatesWithUploadResult, { query }, { signal }),
		enabled,
	});
}

export const fetchGMBTemplate = async (templateId: number) => {
	return apiRequest(GetGMBTemplate, { params: { templateId } });
};

export function useGoogleMyBusinessTemplateQuery(templateId: number) {
	return useQuery({
		queryKey: gmbTemplateKeys.view(templateId),
		queryFn: ({ signal }) =>
			apiRequest(GetGMBTemplate, { params: { templateId } }, { signal }),
	});
}

export function useGoogleMyBusinessLocationsQuery() {
	return useQuery({
		queryKey: gmbTemplateKeys.locations(),
		queryFn: ({ signal }) =>
			apiRequest(GetGoogleMyBusinessLocations, {}, { signal }),
	});
}

export function useMutationAddGoogleMyBusiness(duplicated?: boolean) {
	const queryClient = useQueryClient();
	const { t } = useTranslation(['facebook', 'common']);

	return useMutation({
		mutationFn: (body: Infer<typeof AddGoogleMyBusiness.req>['body']) =>
			apiRequest(AddGoogleMyBusiness, { body }),

		onSuccess: (template) => {
			queryClient.setQueryData(gmbTemplateKeys.view(template.id), template);
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.allLists(),
			});
			if (duplicated) {
				createSuccessToast(t('common:templateDuplicatedSuccessfully'));
			} else {
				createSuccessToast(t('facebook:success.savingTemplate'));
			}
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('facebook:error.savingTemplate'),
			});
		},
	});
}

interface EditGoogleMyBusinessParams {
	templateId: number;
	body: Infer<typeof EditGMBTemplate.req>['body'];
}
export function useMutationEditGoogleMyBusiness() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ templateId, body }: EditGoogleMyBusinessParams) =>
			apiRequest(EditGMBTemplate, {
				params: { templateId },
				body,
			}),

		onSuccess: (_res, { templateId }) => {
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.allLists(),
			});
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.view(templateId),
			});
			createSuccessToast('Template saved.');
		},
	});
}

export function useDeleteGoogleMyBusinessTemplatesMutation() {
	const { t } = useTranslation('facebook');
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (templateIds: number[]) =>
			apiRequest(DeleteGoogleMyBusinessTemplates, { body: { templateIds } }),
		onSuccess: (_data, templateIds) => {
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.allLists(),
			});
			createSuccessToast(
				t('success.deletingTemplate', { count: templateIds.length })
			);
		},
		onError: (err, templateIds) => {
			handleRequestErrors([err], {
				errorMessage: t('error.deletingTemplate', {
					count: templateIds.length,
				}),
			});
		},
	});
}

export function useUploadGoogleMyBusinessTemplateMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ templateId }: { templateId: number }) =>
			apiRequest(UploadGoogleMyBusinessTemplate, { params: { templateId } }),
		onSuccess: (template) => {
			queryClient.setQueryData(
				gmbTemplateKeys.uploadTemplate(template.id),
				template
			);
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.allLists(),
			});
			createSuccessToast('Beginning GMB template upload...');
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: 'Error while starting upload, an upload is in progess.',
			});
		},
	});
}

export function useScheduleGoogleMyBusinessTemplateMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			templateId,
			scheduledPublishAt,
		}: {
			templateId: number;
			scheduledPublishAt: string;
		}) =>
			apiRequest(ScheduleGoogleMyBusinessTemplate, {
				params: { templateId },
				body: { scheduledPublishAt },
			}),
		onSuccess: (_template, { templateId }) => {
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.allLists(),
			});
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.view(templateId),
			});
			createSuccessToast('GMB template scheduled.');
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: 'Error while scheduling the GMB template.',
			});
		},
	});
}

export function useCancelScheduledGoogleMyBusinessTemplateMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ templateId }: { templateId: number }) =>
			apiRequest(CancelScheduledGoogleMyBusinessTemplate, {
				params: { templateId },
			}),
		onSuccess: (_template, { templateId }) => {
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.allLists(),
			});
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.view(templateId),
			});
			createSuccessToast('Scheduled GMB template canceled.');
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: 'Error while canceling the scheduled GMB template.',
			});
		},
	});
}

export function useGMBTemplateNameUniquenessMutation() {
	return useMutation({
		mutationFn: (body: AddGMBTemplateDto | EditGMBTemplateDto) =>
			apiRequest(CheckGMBTemplateUniqueNamesValidation, { body }),
	});
}

export function useGMBTemplateHistoryQuery(
	templateId: number,
	query: ListGMBTemplatesHistoryQuery,
	enabled = true
) {
	return useQuery({
		queryKey: gmbTemplateKeys.history(templateId, query),
		queryFn: ({ signal }) =>
			apiRequest(
				GetGMBTemplateHistory,
				{
					params: { templateId },
					query,
				},
				{ signal }
			),
		enabled,
	});
}
